import React, { useState } from "react";
import {
  Database,
  Table,
  Eye,
  FunctionSquare,
  Workflow,
  ChevronRight,
  ChevronDown,
  FileText,
  Code2,
  FolderTree,
  Upload,
  Download,
} from "lucide-react";

const databases = [
  {
    name: "ecommerce_db",

    procedures: [
      {
        name: "sp_create_order",
        params: ["user_id", "product_id", "qty"],
      },
      {
        name: "sp_update_stock",
        params: ["product_id", "stock"],
      },
    ],

    functions: [
      {
        name: "fn_total_sales",
        returns: "DECIMAL",
      },
      {
        name: "fn_user_orders",
        returns: "INT",
      },
    ],

    tables: [
      {
        name: "users",
        columns: ["id", "name", "email", "created_at"],
      },
      {
        name: "orders",
        columns: ["id", "user_id", "amount", "status"],
      },
      {
        name: "products",
        columns: ["id", "title", "price", "stock"],
      },
    ],

    views: [
      {
        name: "vw_sales_report",
        fields: ["month", "total_sales"],
      },
      {
        name: "vw_active_users",
        fields: ["id", "name", "last_login"],
      },
    ],
  },

  {
    name: "school_db",

    procedures: [
      {
        name: "sp_add_student",
        params: ["name", "grade"],
      },
    ],

    functions: [
      {
        name: "fn_total_students",
        returns: "INT",
      },
    ],

    tables: [
      {
        name: "students",
        columns: ["id", "full_name", "grade"],
      },
      {
        name: "teachers",
        columns: ["id", "name", "department"],
      },
    ],

    views: [
      {
        name: "vw_top_students",
        fields: ["student_name", "score"],
      },
    ],
  },
];

const topTabs = [
  { name: "Browse", icon: <FileText size={15} /> },
  { name: "SQL", icon: <Code2 size={15} /> },
  { name: "Structure", icon: <FolderTree size={15} /> },
  { name: "Import", icon: <Upload size={15} /> },
  { name: "Export", icon: <Download size={15} /> },
];

export default function PhpMyAdmin() {
  const [openDb, setOpenDb] = useState(null);
  const [openCategory, setOpenCategory] = useState({});
  const [openObject, setOpenObject] = useState({});
  const [activeTab, setActiveTab] = useState("Browse");

  const toggleCategory = (dbIndex, category) => {
    const key = `${dbIndex}-${category}`;

    setOpenCategory((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const toggleObject = (key) => {
    setOpenObject((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const renderTree = (items, type, dbIndex) => {
    return items.map((item, index) => {
      const objectKey = `${dbIndex}-${type}-${index}`;

      return (
        <div key={objectKey}>
          <button
            onClick={() => toggleObject(objectKey)}
            className="flex items-center w-full px-2 py-1 hover:bg-gray-200 rounded"
          >
            {openObject[objectKey] ? (
              <ChevronDown size={13} />
            ) : (
              <ChevronRight size={13} />
            )}

            {type === "tables" && (
              <Table size={14} className="mx-2 text-green-700" />
            )}

            {type === "views" && (
              <Eye size={14} className="mx-2 text-purple-700" />
            )}

            {type === "functions" && (
              <FunctionSquare
                size={14}
                className="mx-2 text-blue-700"
              />
            )}

            {type === "procedures" && (
              <Workflow size={14} className="mx-2 text-orange-700" />
            )}

            <span>{item.name}</span>
          </button>

          {openObject[objectKey] && (
            <div className="ml-8 mt-1">
              {type === "tables" &&
                item.columns.map((col) => (
                  <div
                    key={col}
                    className="px-2 py-1 text-gray-700 hover:bg-gray-100 rounded"
                  >
                    • {col}
                  </div>
                ))}

              {type === "views" &&
                item.fields.map((field) => (
                  <div
                    key={field}
                    className="px-2 py-1 text-gray-700 hover:bg-gray-100 rounded"
                  >
                    • {field}
                  </div>
                ))}

              {type === "functions" && (
                <div className="px-2 py-1 text-gray-700">
                  Returns → {item.returns}
                </div>
              )}

              {type === "procedures" &&
                item.params.map((param) => (
                  <div
                    key={param}
                    className="px-2 py-1 text-gray-700"
                  >
                    • {param}
                  </div>
                ))}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div className="flex h-screen bg-[#eef2f5] text-sm">
      {/* SIDEBAR */}
      <aside className="w-80 bg-[#f8f9fb] border-r border-gray-300 overflow-auto">
        <div className="p-4 border-b bg-white shadow-sm">
          <h1 className="text-2xl font-bold text-[#f8981d]">
            php<span className="text-[#6b7d9b]">MyAdmin</span>
          </h1>
        </div>

        <div className="p-2">
          {databases.map((db, dbIndex) => (
            <div key={db.name} className="mb-2">
              {/* DATABASE */}
              <button
                onClick={() =>
                  setOpenDb(openDb === dbIndex ? null : dbIndex)
                }
                className="flex items-center w-full px-2 py-2 rounded hover:bg-gray-200"
              >
                {openDb === dbIndex ? (
                  <ChevronDown size={15} />
                ) : (
                  <ChevronRight size={15} />
                )}

                <Database
                  size={16}
                  className="mx-2 text-[#3b6ea5]"
                />

                <span className="font-medium">{db.name}</span>
              </button>

              {/* DATABASE CONTENT */}
              {openDb === dbIndex && (
                <div className="ml-5 mt-1 space-y-1">
                  {/* Procedures */}
                  <div>
                    <button
                      onClick={() =>
                        toggleCategory(dbIndex, "procedures")
                      }
                      className="flex items-center w-full px-2 py-1 hover:bg-gray-200 rounded"
                    >
                      {openCategory[
                        `${dbIndex}-procedures`
                      ] ? (
                        <ChevronDown size={13} />
                      ) : (
                        <ChevronRight size={13} />
                      )}

                      <Workflow
                        size={14}
                        className="mx-2 text-orange-700"
                      />

                      Procedures
                    </button>

                    {openCategory[
                      `${dbIndex}-procedures`
                    ] && (
                      <div className="ml-5">
                        {renderTree(
                          db.procedures,
                          "procedures",
                          dbIndex
                        )}
                      </div>
                    )}
                  </div>

                  {/* Functions */}
                  <div>
                    <button
                      onClick={() =>
                        toggleCategory(dbIndex, "functions")
                      }
                      className="flex items-center w-full px-2 py-1 hover:bg-gray-200 rounded"
                    >
                      {openCategory[
                        `${dbIndex}-functions`
                      ] ? (
                        <ChevronDown size={13} />
                      ) : (
                        <ChevronRight size={13} />
                      )}

                      <FunctionSquare
                        size={14}
                        className="mx-2 text-blue-700"
                      />

                      Functions
                    </button>

                    {openCategory[
                      `${dbIndex}-functions`
                    ] && (
                      <div className="ml-5">
                        {renderTree(
                          db.functions,
                          "functions",
                          dbIndex
                        )}
                      </div>
                    )}
                  </div>

                  {/* Tables */}
                  <div>
                    <button
                      onClick={() =>
                        toggleCategory(dbIndex, "tables")
                      }
                      className="flex items-center w-full px-2 py-1 hover:bg-gray-200 rounded"
                    >
                      {openCategory[`${dbIndex}-tables`] ? (
                        <ChevronDown size={13} />
                      ) : (
                        <ChevronRight size={13} />
                      )}

                      <Table
                        size={14}
                        className="mx-2 text-green-700"
                      />

                      Tables
                    </button>

                    {openCategory[`${dbIndex}-tables`] && (
                      <div className="ml-5">
                        {renderTree(
                          db.tables,
                          "tables",
                          dbIndex
                        )}
                      </div>
                    )}
                  </div>

                  {/* Views */}
                  <div>
                    <button
                      onClick={() =>
                        toggleCategory(dbIndex, "views")
                      }
                      className="flex items-center w-full px-2 py-1 hover:bg-gray-200 rounded"
                    >
                      {openCategory[`${dbIndex}-views`] ? (
                        <ChevronDown size={13} />
                      ) : (
                        <ChevronRight size={13} />
                      )}

                      <Eye
                        size={14}
                        className="mx-2 text-purple-700"
                      />

                      Views
                    </button>

                    {openCategory[`${dbIndex}-views`] && (
                      <div className="ml-5">
                        {renderTree(
                          db.views,
                          "views",
                          dbIndex
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </aside>

      {/* MAIN */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* TOP NAV */}
        <header className="bg-white border-b border-gray-300 px-4 py-3 shadow-sm">
          <div className="flex items-center gap-2">
            {topTabs.map((tab) => (
              <button
                key={tab.name}
                onClick={() => setActiveTab(tab.name)}
                className={`flex items-center gap-2 px-4 py-2 rounded border text-sm
                ${
                  activeTab === tab.name
                    ? "bg-[#d9e7f7] border-[#7da2ce] text-[#204b78]"
                    : "bg-gray-50 border-gray-300 hover:bg-gray-100"
                }`}
              >
                {tab.icon}
                {tab.name}
              </button>
            ))}
          </div>
        </header>

        {/* CONTENT */}
        <section className="flex-1 overflow-auto p-6">
          <div className="bg-white border rounded shadow-sm">
            <div className="px-6 py-4 border-b bg-gradient-to-r from-[#f8f8f8] to-[#e7edf6]">
              <h2 className="text-xl font-semibold text-[#2c4b73]">
                phpMyAdmin Dashboard
              </h2>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-blue-50 border rounded p-4">
                  <div className="text-gray-500 text-sm">
                    Databases
                  </div>
                  <div className="text-3xl font-bold mt-2">
                    2
                  </div>
                </div>

                <div className="bg-green-50 border rounded p-4">
                  <div className="text-gray-500 text-sm">
                    Tables
                  </div>
                  <div className="text-3xl font-bold mt-2">
                    5
                  </div>
                </div>

                <div className="bg-orange-50 border rounded p-4">
                  <div className="text-gray-500 text-sm">
                    Procedures
                  </div>
                  <div className="text-3xl font-bold mt-2">
                    3
                  </div>
                </div>

                <div className="bg-purple-50 border rounded p-4">
                  <div className="text-gray-500 text-sm">
                    Views
                  </div>
                  <div className="text-3xl font-bold mt-2">
                    3
                  </div>
                </div>
              </div>

              {/* SQL PANEL */}
              {activeTab === "SQL" && (
                <div className="mt-8">
                  <textarea
                    className="w-full h-44 border rounded p-4 font-mono"
                    defaultValue={`SELECT * FROM users;`}
                  />

                  <button className="mt-3 px-5 py-2 bg-[#3b6ea5] text-white rounded hover:bg-[#2f5b87]">
                    Run Query
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}